/**
 * Deep-link preservation across sign-in.
 *
 * When AuthProvider redirects an unauthenticated visitor to Entra, it hands
 * `signinRedirect()` the path the visitor was actually trying to reach (see
 * `captureRedirectState`). `oidc-client-ts` round-trips that value as opaque
 * `state` through Cognito/Entra and hands it back on `User` from
 * `signinRedirectCallback()`. AuthCallback then feeds it through
 * `sanitizeRedirectTarget` before navigating.
 *
 * `state` crosses the browser and two third-party identity providers before
 * we see it again, so it must be treated as attacker-controlled input on the
 * way back — never passed to `navigate()` unchecked. `sanitizeRedirectTarget`
 * is the sole gate: it accepts only a same-origin relative path and falls
 * back to `/` for anything else, which is what keeps this feature from
 * becoming an open redirect.
 */

// Routes that run their own redirect side-effect and must never become a
// post-login destination — landing back on one of these would immediately
// re-trigger its own redirect (a loop). Shared between AuthProvider (which
// must not *capture* one of these as the pre-login location) and
// sanitizeRedirectTarget (which must not *return* one, in case state was
// forged or stale).
export const AUTH_EXEMPT_PATHS = ['/auth/callback', '/auth/post-logout', '/auth/graph-callback'];

const DEFAULT_TARGET = '/';

/**
 * Captures "where the user was going" right before `signinRedirect()` sends
 * them to Entra. Returns `undefined` (no state to attach) when the current
 * location is itself one of the auth-flow routes — that can happen if, say,
 * a stale/expired session causes AuthProvider to re-enter the sign-in branch
 * while transiently on an auth route.
 */
export function captureRedirectState(location: Pick<Location, 'pathname' | 'search' | 'hash'> = window.location): string | undefined {
  if (AUTH_EXEMPT_PATHS.includes(location.pathname)) return undefined;
  return `${location.pathname}${location.search}${location.hash}`;
}

/**
 * Validates a value that came back as `User.state` from
 * `signinRedirectCallback()` and returns a safe target for `navigate()`.
 *
 * Accepts only a same-origin relative path: it must begin with a single `/`
 * (never `//`, and never `/\` — browsers normalize a leading backslash to a
 * slash, so `/\evil.com` is the same protocol-relative bypass as `//evil.com`
 * in disguise) and must not resolve to a different origin or to one of the
 * auth-flow routes themselves. Anything that doesn't satisfy every check
 * falls back to `/`.
 */
export function sanitizeRedirectTarget(candidate: unknown): string {
  if (typeof candidate !== 'string' || candidate.length === 0) return DEFAULT_TARGET;

  // Must be exactly one leading slash. A second `/` or a `\` immediately
  // after it is a protocol-relative URL (or the backslash variant of one),
  // which is an open redirect off this origin.
  if (candidate[0] !== '/') return DEFAULT_TARGET;
  if (candidate.length > 1 && (candidate[1] === '/' || candidate[1] === '\\')) return DEFAULT_TARGET;

  // Belt-and-suspenders same-origin check: resolve it against the current
  // origin and confirm it didn't escape (covers normalization tricks beyond
  // the two checked above). Reject anything that fails to parse at all.
  let resolved: URL;
  try {
    resolved = new URL(candidate, window.location.origin);
  } catch {
    return DEFAULT_TARGET;
  }
  if (resolved.origin !== window.location.origin) return DEFAULT_TARGET;

  // Never land back on an auth-flow route — that would loop.
  if (AUTH_EXEMPT_PATHS.includes(resolved.pathname)) return DEFAULT_TARGET;

  return candidate;
}
